import { BaseParser, ParsedTransaction } from '../BaseParser';

/**
 * Chase Bank Parser
 * Handles both Chase Credit Card and Chase Bank Statement formats
 */
export class ChaseParser extends BaseParser {
  constructor() {
    super();
    this.name = 'ChaseParser';
    this.priority = 10;
  }

  /**
   * Detect if this is a Chase CSV file
   */
  detect(header: string, bank: string): number {
    let confidence = 0;

    // Bank hint
    if (bank === 'chase') {
      confidence += 50;
    }

    // Chase Credit Card: Transaction Date, Post Date, Description, Category, Type, Amount, Memo
    if (header.includes('transaction date') && header.includes('post date')) {
      confidence += 50;
    }

    // Chase Bank: Details, Posting Date, Description, Amount, Type, Balance, Check or Slip #
    if (header.includes('details') && header.includes('posting date') && header.includes('balance')) {
      confidence += 50;
    }

    return confidence;
  }

  /**
   * Parse Chase CSV
   */
  parse(lines: string[], filename: string): ParsedTransaction[] {
    if (lines.length < 2) return [];

    const header = lines[0].toLowerCase();

    // Determine which Chase format
    if (header.includes('transaction date') && header.includes('post date')) {
      return this.parseChaseCredit(lines, filename);
    } else if (header.includes('details') && header.includes('posting date')) {
      return this.parseChaseBank(lines, filename);
    }

    this.log('Unknown Chase format', 'warn');
    return [];
  }

  /**
   * Parse Chase Credit Card format
   * Format: Transaction Date, Post Date, Description, Category, Type, Amount, Memo
   */
  private parseChaseCredit(lines: string[], filename: string): ParsedTransaction[] {
    const transactions: ParsedTransaction[] = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (!line.trim()) continue;

      try {
        const row = this.parseCSVLine(line);
        if (row.length < 6) continue;

        const [transactionDate, postDate, description, category, type, amount, memo] = row;

        transactions.push(
          this.createTransaction({
            id: this.generateId(postDate, description, amount),
            date: this.parseDate(transactionDate).toISOString(),
            postDate: this.parseDate(postDate).toISOString(),
            description: description || 'Unknown',
            amount: this.parseAmount(amount),
            type: type || 'Sale',
            category: this.inferCategory(description, type),
            memo: memo || '',
            source: filename || 'Chase Credit Card',
            rawData: row
          })
        );
      } catch (error) {
        this.log(`Error parsing line ${i}: ${(error as Error).message}`, 'error');
      }
    }

    return transactions;
  }

  /**
   * Parse Chase Bank Statement format
   * Format: Details, Posting Date, Description, Amount, Type, Balance, Check or Slip #
   */
  private parseChaseBank(lines: string[], filename: string): ParsedTransaction[] {
    const transactions: ParsedTransaction[] = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (!line.trim()) continue;

      try {
        const row = this.parseCSVLine(line);
        if (row.length < 5) continue;

        const [details, postingDate, description, amount, type, balance, checkNum] = row;

        transactions.push(
          this.createTransaction({
            id: this.generateId(postingDate, description, amount),
            date: this.parseDate(postingDate).toISOString(),
            postDate: this.parseDate(postingDate).toISOString(),
            description: description || 'Unknown',
            amount: this.parseAmount(amount),
            type: type || 'Unknown',
            category: this.inferCategory(description, type),
            memo: details || '',
            balance: this.parseAmount(balance),
            source: filename || 'Chase Bank',
            rawData: row
          })
        );
      } catch (error) {
        this.log(`Error parsing line ${i}: ${(error as Error).message}`, 'error');
      }
    }

    return transactions;
  }

  /**
   * Infer category from description and type
   * This is a simplified version - will be replaced by the categorization pipeline
   */
  private inferCategory(description: string, type: string): string {
    const desc = description.toLowerCase();

    // Credit Card Payments
    if (
      type === 'Payment' &&
      (desc.includes('automatic payment') ||
        desc.includes('cardmember serv') ||
        desc.includes('amex') ||
        desc.includes('american express') ||
        desc.includes('chase cc'))
    ) {
      return 'Credit Card Payment';
    }

    // Basic categorization - will be enhanced by pipeline
    return 'Uncategorized';
  }

  getInfo() {
    return {
      name: this.name,
      priority: this.priority,
      supportedBanks: ['chase'],
      formats: ['Chase Credit Card', 'Chase Bank Statement']
    };
  }
}
